import {
  CollectionReference,
  QueryDocumentSnapshot,
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
} from 'firebase/firestore'
import { orderBy } from 'lodash'
import moment from 'moment'
import FirestoreUser from '../types/FirestoreUser'
import { MonthlyReport } from '../store/useStore'
import { userHasStudy } from './studyHelpers'
import { ALS_DIAGNOSES, PATIENT_ROLE } from '../constants/completion'
import { primaryServices } from './firebaseServiceContext'

export type CompletionData = {
  complete: QueryDocumentSnapshot<FirestoreUser>[]
  incomplete: QueryDocumentSnapshot<FirestoreUser>[]
}

export type ButtonType = 'current' | 'last' | 'last2' | 'last3' | 'last6' | null

/**
 * Fetches completion data for a given month and filters.
 * Returns arrays of complete and incomplete user documents.
 *
 * @param month - The target month in YYYY-MM format
 * @param selectedButton - Which time range button was selected (affects multi-month queries)
 * @param includeAllRoles - If true, includes admins and researchers; if false, only patients
 * @param selectedStudy - The study NAME (not ID) to filter by, or null for all studies
 * @returns Object containing arrays of complete and incomplete user documents
 *
 * Performance note: When selectedStudy is provided, this function fetches all users
 * and filters in memory. Consider using Firestore composite indexes for better performance.
 */
export async function fetchCompletionData(
  month: string,
  selectedButton: ButtonType,
  includeAllRoles: boolean = false,
  selectedStudy: string | null = null
): Promise<CompletionData> {
  const months = [month]

  // Build list of months to check based on button selection
  if (selectedButton === 'last3') {
    months.push(
      moment(month).subtract(1, 'month').format('YYYY-MM'),
      moment(month).subtract(2, 'months').format('YYYY-MM')
    )
  } else if (selectedButton === 'last6') {
    for (let i = 1; i <= 5; i++) {
      months.push(moment(month).subtract(i, 'month').format('YYYY-MM'))
    }
  }

  // Build the users query depending on the flags
  const usersCollection = collection(
    primaryServices.firestore,
    'users'
  ) as CollectionReference<FirestoreUser>

  // We can only use one array-contains per query, so we prioritize study filter
  // and filter by roles and profile in memory if needed
  let usersQuery
  if (selectedStudy) {
    // If a study is selected, we need to fetch all users and filter in memory
    // because studies can be stored as strings OR objects (StudyData format)
    // and array-contains doesn't work well with both formats
    usersQuery = query(usersCollection, where('profile', '!=', null))
  } else {
    // No study filter, use original logic
    usersQuery = includeAllRoles
      ? query(usersCollection, where('profile', '!=', null))
      : query(
          usersCollection,
          where('roles', 'array-contains', 'patient'),
          where('profile', '!=', null)
        )
  }

  const patientsSnap = await getDocs(usersQuery)
  let patients = patientsSnap.docs

  // Optimized: Combine all filters into single pass for better performance
  // This reduces array iterations from 3 to 1, improving CPU cache utilization
  patients = patients.filter((doc) => {
    const user = doc.data()

    // Filter 1: Study membership (if a study is selected)
    if (selectedStudy && !userHasStudy(user, selectedStudy)) {
      return false
    }

    // Filter 2: Role filtering (patient only when study selected and not includeAllRoles)
    if (
      selectedStudy &&
      !includeAllRoles &&
      !user.roles?.includes(PATIENT_ROLE)
    ) {
      return false
    }

    // Filter 3: ALS-related diagnoses (only when not includeAllRoles)
    if (!includeAllRoles) {
      const diagnosis = user.profile?.diagnosis
      if (!diagnosis || !ALS_DIAGNOSES.includes(diagnosis as any)) {
        return false
      }
    }

    return true
  })

  // Fetch reports for all months
  const reports = await Promise.all(
    months.map((m) =>
      getDocs(
        query(
          collectionGroup(
            primaryServices.firestore,
            'reports'
          ) as CollectionReference<MonthlyReport>,
          where('id', '==', m)
        )
      )
    )
  )

  // Determine which users have complete reports
  const uidsWithCompleteReports: string[] = []

  reports.forEach((reportSnap) => {
    reportSnap.forEach((doc) => {
      const report = doc.data()
      const uid = doc.ref.parent.parent?.id
      if (!uid) {
        throw new Error(
          'Could not get uid for report doc with path ' + doc.ref.path
        )
      }

      if (isReportComplete(report)) {
        uidsWithCompleteReports.push(uid)
      }
    })
  })

  // Separate users into complete and incomplete
  const docs: CompletionData = {
    complete: [],
    incomplete: [],
  }

  patients.forEach((doc) => {
    if (uidsWithCompleteReports.includes(doc.id)) {
      docs.complete.push(doc)
    } else {
      docs.incomplete.push(doc)
    }
  })

  // Sort by last name, first name
  docs.complete = orderBy(docs.complete, (doc) => {
    const data = doc.data()
    return `${data.profile?.searchLastName}, ${data.profile?.searchFirstName}`
  })

  docs.incomplete = orderBy(docs.incomplete, (doc) => {
    const data = doc.data()
    return `${data.profile?.searchLastName}, ${data.profile?.searchFirstName}`
  })

  return docs
}

/**
 * Checks if a monthly report is complete based on required fields.
 */
function isReportComplete(report: MonthlyReport): boolean {
  if (report.alsfrs?.score === undefined) return false
  if (report.alssq?.score === undefined) return false
  if (!report.clinicalObservations) return false
  if (!report.diets) return false
  if (!report.mentalStrategies) return false
  if (!report.nonSupplementMedications) return false
  if (!report.panas?.modifiedPanas?.complete && !report.panas?.main?.complete) {
    return false
  }
  if (!report.physicalStrategies) return false
  if (!report.stool?.stool?.complete) return false
  if (!report.stool?.urine?.complete) return false
  if (!report.supplements) return false
  if (report.vitalStats?.weight === undefined) return false

  return true
}
